import Accordion from "@/common/components/Accordion";
import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import Checkbox from "@/common/components/Checkbox";
import { MultipleDocumentUploader } from "@/common/components/MultipleDocumentUploader";
import { MultipleImageUploader } from "@/common/components/MultipleImageUploader";
import SelectController from "@/common/components/SelectController";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { Mapper } from "@/common/services/mapper";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import useEquipment from "../../hooks/useEquipment";
import { useCompanyBranches } from "@/common/hooks/queries/useCompanyBranches";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { useEffect, useMemo, useState } from "react";
import { equipmentSchema, EquipmentValidationSchema } from "../../schemas/create.schema";
import { ShowHeader } from "@/common/components/ShowHeader";
import { PeriodRestriction } from "../../components/form/PeriodRestriction";
import { EquipmentDetails } from "../../components/form/EquipmentDetails";
import { ApplicableProducts } from "../../components/form/ApplicableProducts";
import { companyEquipmentDefaultValues } from "../../utils/companyEquipmentDefaultValues";
import useUpdateEquipment from "../../hooks/useUpdateEquipment";
import Spinner from "@/common/components/Spinner";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useMineCompany } from "@/features/company/hooks/useMineCompany";
import { ReasonValidationSchema } from "@/common/schemas/reason.schema";
import ReasonModal from "@/common/components/ReasonModal";
import { type Option } from '@/common/types/option'
import { CarFrontIcon } from "lucide-react";
import { EquipmentsTransformer } from "@/features/equipments/services/equipmentTransformer";

const EquipmentsUpdate = () => {
  const { id: equipmentId } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [showReasonModal, setShowReasonModal] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { data: company } = useMineCompany();

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs();

  const {
    data: equipment,
    isFetched: equipmentIsFetched,
    isPending: equipmentIsPending,
    dataUpdatedAt: equipmentUpdatedAt,
  } = useEquipment({ id: equipmentId || '' });

  const categoryFieldForDisplayName = equipment?.model?.category?.field_for_display;

  const equipmentDisplayName = useMemo(() => {
    return (categoryFieldForDisplayName && equipment[categoryFieldForDisplayName as keyof typeof equipment] as string) ||
      equipment?.license_plate ||
      equipment?.official_name ||
      equipment?.serial_number ||
      'Não informado';
  }, [equipmentUpdatedAt]);

  const [searchCompanyBranch, setSearchCompanyBranch] = useState('');

  const { data: companyBranchOptions } = useCompanyBranches({
    filters: { search: searchCompanyBranch || '' },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    reset,
    formState: { errors }
  } = useForm<EquipmentValidationSchema>({
    resolver: zodResolver(equipmentSchema),
    defaultValues: companyEquipmentDefaultValues({
      equipmentData: equipment,
      companyBranchOptions: companyBranchOptions || [],
    })
  });

  const { mutate: updateCompanyEquipment, isPending: updateCompanyEquipmentIsPending } = useUpdateEquipment({
    equipmentId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyEquipments'] });
      navigate(`/equipments/${equipmentId}`);
    },
    onError: FormTransformers.errorsTransformer<EquipmentValidationSchema>({
      setError,
      setMessages,
      transformer: EquipmentsTransformer.errorsTransformer(),
    }),
  });

  const onSubmit = (data: EquipmentValidationSchema, reasonData?: ReasonValidationSchema) => {
    updateCompanyEquipment({
      form: data,
      reasonForm: reasonData,
      equipmentData: equipment,
    });
  };

  const documents = watch('documents') || [];

  useEffect(() => {
    if (equipmentIsFetched && equipment?.branch) {
      const branchOption = [{
        label: equipment.branch.display_name,
        value: equipment.branch.id
      }] as Option[]

      reset(companyEquipmentDefaultValues({
        equipmentData: equipment,
        companyBranchOptions: branchOption,
      }))
    }
  }, [equipmentIsFetched]);

  return (
    <form
      onSubmit={handleSubmit((data) => {
        if (company?.update_equipment_log_reason_requirement === 'required') {
          setShowReasonModal(true);
        } else {
          onSubmit(data);
        }
      })}
    >
      <ShowHeader
        backTo={`/equipments/${equipmentId}`}
        logoUrl={equipment?.photos?.[0]?.url}
        isLoading={equipmentIsPending}
        title={(
          <div className='flex items-center gap-1.5'>
            {categoryFieldForDisplayName === 'license_plate' && (
              <CarFrontIcon strokeWidth={1.5} size={18} />
            )}

            <h2>
              {equipmentDisplayName}
            </h2>
          </div>
        )}
        buttons={(
          <div className="flex gap-x-4 items-center">
            <Link to={`/equipments/${equipmentId}`}>
              <Button variant="outline-error">Cancelar</Button>
            </Link>
            <Button type="submit" name="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" disabled={uploadBlobIsPending}>
              {updateCompanyEquipmentIsPending ? <Spinner className="h-6 mx-[23px] fill-white-800" /> : 'Salvar'}
            </Button>
          </div>
        )}
      />

      <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto max-h-[calc(100vh-128px)]">
        <p className="text-lg font-bold">Dados do equipamento</p>
        <MultipleImageUploader
          label="Anexe uma foto"
          name="equipment-images"
          accept="image/*"
          uploadBlob={uploadBlob}
          control={control}
          fieldName="photos"
          disabled={uploadBlobIsPending || updateCompanyEquipmentIsPending}
        />

        <SelectController
          control={control}
          name="branch_id"
          label="Filial"
          markAsRequired
          onInputChange={setSearchCompanyBranch}
          options={companyBranchOptions || []}
          errorMessage={errors.branch_id?.message || undefined}
          isDisabled={updateCompanyEquipmentIsPending}
        />

        <PeriodRestriction
          control={control}
          register={register}
          watch={watch}
          errors={errors}
          disabled={updateCompanyEquipmentIsPending}
        />

        <EquipmentDetails
          equipment={equipment}
          control={control}
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          disabled={updateCompanyEquipmentIsPending}
          updateDisabled={equipment?.model?.category?.supports_infocar_data_enrichment}
        />

        <Accordion
          head={
            <Checkbox
              label="Documentos do equipamento"
              name="equipment_documents"
              boxClassName="w-[24px] h-[24px]"
              checked={documents.length > 0}
              disabled
            />
          }
          body={(
            <div className="flex w-full flex-col gap-4 text-white-800">
              <MultipleDocumentUploader
                title="Anexe um documento do equipamento"
                fieldName="documents"
                name="equipment-documents"
                accept="application/pdf"
                uploadBlob={uploadBlob}
                control={control}
                disabled={uploadBlobIsPending || updateCompanyEquipmentIsPending}
              />
            </div>
          )}
        />

        <ApplicableProducts
          cardId={equipment?.card?.id}
          control={control}
          register={register}
          watch={watch}
          errors={errors}
          disabled={updateCompanyEquipmentIsPending}
        />
      </Box>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}

      <ReasonModal
        isOpen={showReasonModal}
        setIsOpen={setShowReasonModal}
        onSubmit={(reasonData) => handleSubmit((data) => onSubmit(data, reasonData))()}
      />
    </form>
  );
};

export default EquipmentsUpdate;
